import { Box, IconButton, InputBase, Typography, alpha } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { getSegmentColor, digitsToNumber } from "./factoraje.helpers";

export interface EmpresaLinea {
  /** Id local de la fila (estable para React). */
  id: string;
  /** Id del deudor en el servidor; vacío mientras la empresa no se ha guardado. */
  debtorId?: string;
  nombre: string;
  rfc: string;
  monto: number;
}

interface Props {
  empresa: EmpresaLinea;
  /** Posición en la lista: define el color del chip, igual que su segmento en la barra. */
  index: number;
  onChange: (patch: Partial<EmpresaLinea>) => void;
  onRemove: () => void;
  /** Marca el nombre como faltante (tras intentar continuar sin él). */
  nombreFaltante: boolean;
  /** Error del RFC capturado (`getRfcEmpresaError`); vacío si es válido o no se capturó. */
  rfcError: string;
}

/** Etiqueta de campo de `.fxu-cli-k`: 9.5px/700 en mayúsculas. */
const labelSx = {
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: "3px",
} as const;

/** Input sin caja de `.fxu-cli-name`: solo una línea inferior al hover/focus. */
const underlineInputSx = (error: boolean) => ({
  width: "100%",
  fontSize: 14,
  fontWeight: 600,
  color: "text.primary",
  borderBottom: "1px solid",
  borderColor: error ? "error.main" : "transparent",
  "&:hover": { borderColor: error ? "error.main" : "#D5DCE4" },
  "&.Mui-focused": { borderColor: error ? "error.main" : "primary.accent" },
  "& input": { p: "2px 0" },
});

/**
 * Replica `.fxu-cli` del diseño de Flux: chip de color, empresa, RFC opcional, monto a ocupar y el
 * botón para quitarla.
 */
export const CardEmpresaLinea = ({ empresa, index, onChange, onRemove, nombreFaltante, rfcError }: Props) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: "14px 16px",
      alignItems: "flex-end",
      bgcolor: "background.paper",
      border: "1px solid #E4E7EC",
      borderRadius: "12px",
      p: "14px 16px",
      "&:focus-within": { borderColor: (theme) => alpha(theme.palette.primary.accent, 0.45) },
    }}
  >
    <Box
      sx={{
        alignSelf: "stretch",
        minHeight: 38,
        width: 4,
        flex: "0 0 4px",
        borderRadius: "3px",
        bgcolor: (theme) => getSegmentColor(theme, index),
      }}
    />

    <Box sx={{ flex: "1 1 200px", minWidth: 160 }}>
      <Typography sx={labelSx}>
        Empresa{" "}
        {nombreFaltante && (
          <Typography component="span" sx={{ ...labelSx, color: "error.main" }}>
            · falta
          </Typography>
        )}
      </Typography>
      <InputBase
        autoComplete="off"
        placeholder="Nombre o razón social"
        value={empresa.nombre}
        onChange={(e) => onChange({ nombre: e.target.value })}
        inputProps={{ "data-testid": `uso-linea-nombre-input-${index}` }}
        sx={underlineInputSx(nombreFaltante)}
      />
    </Box>

    <Box sx={{ flex: "0 1 150px", minWidth: 120 }}>
      <Typography sx={labelSx}>
        RFC{" "}
        <Typography component="span" sx={{ ...labelSx, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
          (opcional)
        </Typography>
      </Typography>
      <InputBase
        autoComplete="off"
        placeholder="ABC123456XYZ"
        value={empresa.rfc}
        onChange={(e) => onChange({ rfc: e.target.value.toUpperCase().replace(/[\s-]/g, "") })}
        inputProps={{ "data-testid": `uso-linea-rfc-input-${index}`, maxLength: 12 }}
        sx={[underlineInputSx(!!rfcError), { fontSize: 12.5, fontWeight: 500, fontVariantNumeric: "tabular-nums" }]}
      />
      {rfcError && (
        <Typography sx={{ fontSize: 11, color: "error.main", mt: "3px" }} data-testid={`uso-linea-rfc-error-${index}`}>
          {rfcError}
        </Typography>
      )}
    </Box>

    <Box sx={{ flex: "0 1 170px", minWidth: 130 }}>
      <Typography sx={labelSx}>Monto a ocupar</Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: "6px",
          borderBottom: "1px solid #D5DCE4",
          "&:focus-within": { borderColor: "primary.accent" },
        }}
      >
        <Typography component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
          $
        </Typography>
        <InputBase
          autoComplete="off"
          placeholder="0"
          value={empresa.monto ? empresa.monto.toLocaleString("en-US") : ""}
          onChange={(e) => onChange({ monto: digitsToNumber(e.target.value) })}
          inputProps={{ "data-testid": `uso-linea-monto-input-${index}`, inputMode: "numeric" }}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 600,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
            "& input": { textAlign: "right", p: "2px 0" },
          }}
        />
      </Box>
    </Box>

    <IconButton
      title="Quitar empresa"
      onClick={onRemove}
      data-testid={`uso-linea-quitar-button-${index}`}
      sx={{
        width: 30,
        height: 30,
        border: "1px solid #E4E7EC",
        borderRadius: "8px",
        color: "text.secondary",
        "&:hover": { bgcolor: "#F2F5F9", color: "text.primary" },
      }}
    >
      <DeleteOutline sx={{ fontSize: 15 }} />
    </IconButton>
  </Box>
);
